import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { FocusOn } from "react-focus-on";
import { navigate } from "gatsby";
import { RCL as useTranslation } from "../../RCL";
import { setLanguageCookie } from "../../../utils/helper";
import { Icon } from "../../Icons";
import { SlideInMenuHeader } from "./Header";
import { SlideInMenuSubMenu } from "./SubMenu";
import { HotelSearchBox } from "../../HotelSearchBox/HotelSearchBox";
import { HeaderGateway } from "../Gateway";
import { LanguageToggle, languageToggleAdapter } from "../../LanguageToggle";
import * as styles from "./SlideInMenu.module.scss";

const SlideInMenu = ({ title, menuLinks, locale, altLangPages }) => {
	const hamburgerMenuButtonRef = useRef(null);
	const [isMenuOpened, setIsMenuOpened] = useState(false);

	const handleToggleMenu = () => {
		setIsMenuOpened(!isMenuOpened);
	};

	const unmountTrap = () => {
		setIsMenuOpened(false);

		// On close menu, set focus back to hamburger menu button
		if (hamburgerMenuButtonRef.current) {
			setTimeout(() => {
				hamburgerMenuButtonRef.current.focus();
			}, 0);
		}
	};

	return (
		<nav aria-label={title}>
			<button
				type="button"
				className={styles.hamburgerMenuButton}
				onClick={handleToggleMenu}
				aria-label={useTranslation({ searchKey: "a11y-open-menu" })}
				aria-expanded={isMenuOpened}
				aria-controls="main-menu-nav"
				ref={hamburgerMenuButtonRef}>
				<Icon name="menu" />
			</button>
			<div id="main-menu-nav" className={cx(styles.wrapper, isMenuOpened && styles.wrapperOpen)}>
				<FocusOn
					enabled={isMenuOpened}
					shouldIgnore={el => !(el.id === "gatsby-focus-wrapper" && el.tabIndex === -1)}
					onClickOutside={handleToggleMenu}
					onEscapeKey={handleToggleMenu}
					className={styles.container}
					gapMode="padding">
					<SlideInMenuHeader
						id="main-menu-nav"
						title={title}
						isOpen={isMenuOpened}
						handleClose={unmountTrap}
					/>

					<HotelSearchBox locale={locale?.substring(0, 2)} />

					<SlideInMenuSubMenu
						menuLinks={menuLinks}
						menuOpenState={isMenuOpened}
						unmountTrap={unmountTrap}
					/>
					<HeaderGateway />
					<LanguageToggle
						handleChangeLanguage={(lang, path) => {
							setLanguageCookie(lang);
							navigate(path);
						}}
						locale={locale}
						altLangPages={languageToggleAdapter(altLangPages)}
						classNames={{
							container: styles.languageSwitcher,
							currentLanguage: styles.languageSwitcherCurrentLanguage,
							altLanguage: styles.languageSwitcherAltLanguage,
						}}
					/>
				</FocusOn>
			</div>
		</nav>
	);
};

SlideInMenu.propTypes = {
	title: PropTypes.string,
	menuLinks: PropTypes.arrayOf(PropTypes.object),
	locale: PropTypes.string,
	altLangPages: PropTypes.shape({
		edges: PropTypes.arrayOf(
			PropTypes.shape({
				node: PropTypes.shape({
					context: PropTypes.shape({
						id: PropTypes.string,
						locale: PropTypes.string,
					}),
					path: PropTypes.string,
				}),
			})
		),
	}).isRequired,
};
SlideInMenu.defaultProps = {
	title: "Menu",
	menuLinks: [],
	locale: null,
};

export default SlideInMenu;
export { SlideInMenu };
